/**
 * FixedExpenseSnapshots — iter132 Task B
 * ────────────────────────────────────────────────────────────────────────
 * Replaces the legacy "Recurring Fixed Expenses Settings" card with an
 * immutable, whole-snapshot versioning model.
 *
 * UX:
 *   - Header shows the CURRENT effective version + monthly total.
 *   - "Create New Version" form lets SA add any number of expense line
 *     items (name + category + amount + frequency). On save, a new
 *     immutable version is created effective TODAY. The previous version
 *     is automatically marked as superseded.
 *   - Past versions are listed below in reverse-chronological order with
 *     their effective_from date. Deletion is SA-only.
 *
 * Wired into the Financials tab via a button placed next to the
 * "Daily Entries — Monthly View" header.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Layers, Plus, Trash2, Calendar, DollarSign,
  ChevronDown, ChevronUp, AlertCircle, Lock, Sparkles,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (v) => `$${(Number(v) || 0).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmt2 = (v) => `$${(Number(v) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const FREQ_OPTIONS = [
  { v: 'weekly', label: 'Weekly', factor: 52 / 12 },
  { v: 'monthly', label: 'Monthly', factor: 1 },
  { v: 'yearly', label: 'Yearly', factor: 1 / 12 },
];

const COMMON_CATEGORIES = [
  'wages', 'rent', 'utilities', 'insurance', 'subscriptions',
  'loan_emi', 'suppliers', 'other',
];

function emptyItem() {
  return { name: '', category: 'rent', amount: '', frequency: 'monthly' };
}

function ItemRow({ idx, item, onChange, onRemove, locked = false }) {
  return (
    <div className="grid grid-cols-12 gap-2 items-end" data-testid={`snapshot-item-row-${idx}`}>
      <div className="col-span-12 md:col-span-4">
        {idx === 0 && <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Name</label>}
        <Input
          value={item.name}
          onChange={(e) => onChange({ ...item, name: e.target.value })}
          placeholder="e.g. Shop Rent, Internet"
          disabled={locked}
          data-testid={`snapshot-item-name-${idx}`}
        />
      </div>
      <div className="col-span-6 md:col-span-2">
        {idx === 0 && <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Category</label>}
        <Input
          value={item.category}
          onChange={(e) => onChange({ ...item, category: e.target.value.trim().toLowerCase() })}
          list={`fes-cats-${idx}`}
          placeholder="rent"
          disabled={locked}
          data-testid={`snapshot-item-category-${idx}`}
        />
        <datalist id={`fes-cats-${idx}`}>
          {COMMON_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </datalist>
      </div>
      <div className="col-span-6 md:col-span-3">
        {idx === 0 && <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Amount ($)</label>}
        <Input
          type="number"
          step="0.01"
          min="0"
          value={item.amount}
          onChange={(e) => onChange({ ...item, amount: e.target.value })}
          placeholder="0.00"
          disabled={locked}
          data-testid={`snapshot-item-amount-${idx}`}
        />
      </div>
      <div className="col-span-10 md:col-span-2">
        {idx === 0 && <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Frequency</label>}
        <Select value={item.frequency} onValueChange={(v) => onChange({ ...item, frequency: v })} disabled={locked}>
          <SelectTrigger data-testid={`snapshot-item-freq-${idx}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {FREQ_OPTIONS.map((f) => (
              <SelectItem key={f.v} value={f.v}>{f.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2 md:col-span-1 flex justify-end">
        {!locked && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            className="h-9 w-9 text-rose-600 hover:bg-rose-50"
            data-testid={`snapshot-item-remove-${idx}`}
            aria-label="Remove"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

function PastVersionCard({ snap, expanded, onToggle, canDelete, onDelete }) {
  const eff = snap.effective_from;
  const supersededOn = snap.superseded_at ? new Date(snap.superseded_at).toLocaleDateString('en-AU') : null;
  const monthly = Number(snap.monthly_total) || 0;
  return (
    <div className={`rounded-lg border ${snap.superseded_at ? 'border-slate-200 bg-slate-50/60' : 'border-emerald-300 bg-emerald-50/40'} overflow-hidden`} data-testid={`snapshot-card-${snap.snapshot_id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-white/60"
        data-testid={`snapshot-toggle-${snap.snapshot_id}`}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1 text-left">
          <div className={`p-2 rounded-lg ${snap.superseded_at ? 'bg-slate-200' : 'bg-emerald-200'}`}>
            <Layers className={`h-4 w-4 ${snap.superseded_at ? 'text-slate-600' : 'text-emerald-700'}`} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-900 flex items-center gap-2 flex-wrap">
              Version {snap.version_no}
              {!snap.superseded_at && (
                <span className="px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[9px] uppercase tracking-wider font-extrabold">Current</span>
              )}
              {snap.source === 'auto_wage_change' && (
                <span className="px-2 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-[9px] uppercase tracking-wider font-bold inline-flex items-center gap-1">
                  <Sparkles className="h-2.5 w-2.5" /> auto · wage change
                </span>
              )}
            </p>
            <p className="text-xs text-slate-500 flex items-center gap-1.5 flex-wrap">
              <Calendar className="h-3 w-3" />
              Effective from {new Date(eff + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}
              {supersededOn && (
                <span className="text-slate-400">· superseded on {supersededOn}</span>
              )}
              <span className="text-slate-400">· {(snap.items || []).length} item{(snap.items || []).length === 1 ? '' : 's'}</span>
            </p>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-base font-black text-slate-900 tabular-nums">{fmt(monthly)}</p>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">per month</p>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {expanded && (
        <div className="border-t bg-white/80 px-4 py-3 space-y-2" data-testid={`snapshot-body-${snap.snapshot_id}`}>
          {snap.notes && (
            <p className="text-xs italic text-slate-600 bg-amber-50 border-l-2 border-amber-300 px-3 py-1.5 rounded">
              {snap.notes}
            </p>
          )}
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              <tr>
                <th className="text-left py-1">Item</th>
                <th className="text-left py-1">Category</th>
                <th className="text-right py-1">Amount</th>
                <th className="text-left py-1 pl-3">Freq</th>
                <th className="text-right py-1">Monthly equiv</th>
              </tr>
            </thead>
            <tbody>
              {(snap.items || []).map((it, i) => {
                const factor = FREQ_OPTIONS.find((f) => f.v === (it.frequency || 'monthly'))?.factor || 1;
                const monthlyEquiv = (Number(it.amount) || 0) * factor;
                return (
                  <tr key={i} className="border-t border-slate-100" data-testid={`snapshot-${snap.snapshot_id}-item-${i}`}>
                    <td className="py-1 font-medium text-slate-900">{it.name}</td>
                    <td className="py-1 text-xs text-slate-500">{it.category || 'other'}</td>
                    <td className="py-1 text-right tabular-nums">{fmt2(it.amount)}</td>
                    <td className="py-1 pl-3 text-xs text-slate-500">{it.frequency}</td>
                    <td className="py-1 text-right tabular-nums font-semibold">{fmt2(monthlyEquiv)}</td>
                  </tr>
                );
              })}
              <tr className="border-t-2 border-slate-300 font-bold">
                <td colSpan={4} className="py-1 text-right text-slate-700 text-xs uppercase">Total monthly</td>
                <td className="py-1 text-right tabular-nums text-emerald-700">{fmt2(monthly)}</td>
              </tr>
            </tbody>
          </table>
          <p className="text-[10px] text-slate-500 italic pt-1">
            Created {new Date(snap.created_at).toLocaleString('en-AU')} by {snap.created_by_name}
          </p>
          {canDelete && (
            <div className="flex justify-end pt-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={onDelete}
                className="h-7 text-xs text-rose-600 hover:bg-rose-50"
                data-testid={`snapshot-delete-${snap.snapshot_id}`}
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Delete this version
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function FixedExpenseSnapshots({ open, onClose, onSaved, defaultEffectiveFrom }) {
  const [list, setList] = useState({ snapshots: [], count: 0, effective_monthly_total: 0 });
  const [loading, setLoading] = useState(true);
  const [draftItems, setDraftItems] = useState([emptyItem()]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  // iter138.9x — Effective From date for the NEW version. Defaults to
  // today (legacy behaviour). SA can backdate to apply the new fixed
  // expense total to Daily Entries in past months.
  const todayIso = new Date().toISOString().slice(0, 10);
  const [effectiveFrom, setEffectiveFrom] = useState(defaultEffectiveFrom || todayIso);

  // Reset effectiveFrom whenever the dialog (re-)opens with a different
  // defaultEffectiveFrom (e.g. user switched month tab before opening).
  useEffect(() => {
    if (open) {
      setEffectiveFrom(defaultEffectiveFrom || todayIso);
    }
     
  }, [open, defaultEffectiveFrom]);

  const fetchList = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/financials/fixed-expense-snapshots`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setList(d);
        // Auto-expand the current version
        if (d.effective_snapshot_id) setExpandedId(d.effective_snapshot_id);
      } else {
        toast.error('Could not load fixed expense versions');
      }
    } catch (_e) { toast.error('Network error'); }
    finally { setLoading(false); }
  }, [open]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Live preview of the draft monthly total
  const draftMonthly = draftItems.reduce((sum, it) => {
    const f = FREQ_OPTIONS.find((x) => x.v === it.frequency)?.factor || 1;
    return sum + (Number(it.amount) || 0) * f;
  }, 0);

  const addRow = () => setDraftItems([...draftItems, emptyItem()]);
  const removeRow = (idx) => setDraftItems(draftItems.filter((_, i) => i !== idx));
  const updateRow = (idx, next) => setDraftItems(draftItems.map((it, i) => i === idx ? next : it));

  const handleSave = async () => {
    const cleaned = draftItems
      .map((it) => ({
        name: (it.name || '').trim(),
        category: (it.category || 'other').trim().toLowerCase() || 'other',
        amount: Number(it.amount) || 0,
        frequency: it.frequency || 'monthly',
      }))
      .filter((it) => it.name && it.amount >= 0);
    if (cleaned.length === 0) {
      toast.error('Add at least one expense item');
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/financials/fixed-expense-snapshots`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cleaned,
          notes: notes.trim() || null,
          // iter138.9x — null sends "today" on the server (legacy default)
          effective_from: effectiveFrom && effectiveFrom !== todayIso ? effectiveFrom : null,
        }),
      });
      if (r.ok) {
        const data = await r.json();
        const v = data.snapshot;
        toast.success(`Version ${v.version_no} created — ${fmt(v.monthly_total)} / month, effective ${new Date(v.effective_from).toLocaleDateString('en-AU')}`);
        setDraftItems([emptyItem()]);
        setNotes('');
        setEffectiveFrom(defaultEffectiveFrom || todayIso);
        await fetchList();
        onSaved?.();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to create version');
      }
    } catch (_e) { toast.error('Network error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (snap) => {
    if (!window.confirm(`Permanently delete Version ${snap.version_no}? This cannot be undone.`)) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/financials/fixed-expense-snapshots/${snap.snapshot_id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (r.ok) {
        toast.success(`Version ${snap.version_no} deleted`);
        await fetchList();
        onSaved?.();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to delete');
      }
    } catch (_e) { toast.error('Network error'); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="fixed-expense-snapshots-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-orange-600" />
            Fixed Expense — Versions
          </DialogTitle>
          <DialogDescription className="text-xs">
            Each version is immutable. Daily Entry takes its fixed-expense share from the version effective on the entry's date.
            New versions default to <strong>effective from today</strong> — but you can <strong>backdate</strong> the Effective From field below to apply the new total to past months too.
          </DialogDescription>
        </DialogHeader>

        {/* Current snapshot summary */}
        <div className={`rounded-xl p-4 border-2 ${list.effective_snapshot_id ? 'border-emerald-300 bg-emerald-50/40' : 'border-amber-300 bg-amber-50/40'}`} data-testid="current-snapshot-summary">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Current monthly fixed expense</p>
              <p className="text-3xl font-black tabular-nums text-slate-900">
                {fmt(list.effective_monthly_total)}
              </p>
              {list.effective_monthly_total > 0 && (
                <p className="text-xs text-slate-600 mt-0.5">
                  Daily split: <strong className="tabular-nums">{fmt2((list.effective_monthly_total || 0) / new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate())}</strong>/day this month
                </p>
              )}
            </div>
            {!list.effective_snapshot_id && (
              <div className="flex items-center gap-2 text-amber-800 bg-amber-100 border border-amber-300 rounded-lg px-3 py-2 text-xs max-w-md">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>No version yet — daily entries below will show <strong>$0</strong> fixed expense until you create v1.</span>
              </div>
            )}
          </div>
        </div>

        {/* Create new version form */}
        <Card className="p-4 bg-slate-50/50 border-slate-200">
          <p className="text-xs uppercase tracking-wider text-slate-600 font-bold flex items-center gap-2 mb-3">
            <Plus className="h-3.5 w-3.5" />
            {list.snapshots.length === 0 ? 'Create version 1' : `Create version ${(list.snapshots[0]?.version_no || 0) + 1}`}
            <span className={`ml-auto text-[10px] font-normal normal-case ${effectiveFrom && effectiveFrom !== todayIso ? 'text-amber-700 font-semibold' : 'text-slate-500'}`}>
              effective {effectiveFrom && effectiveFrom !== todayIso
                ? `from ${new Date(effectiveFrom + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}`
                : 'today'}
            </span>
          </p>
          <div className="space-y-2" data-testid="snapshot-draft-items">
            {draftItems.map((it, i) => (
              <ItemRow
                key={i}
                idx={i}
                item={it}
                onChange={(next) => updateRow(i, next)}
                onRemove={() => removeRow(i)}
              />
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={addRow}
            className="mt-3 h-8 text-xs"
            data-testid="snapshot-add-item-btn"
          >
            <Plus className="h-3 w-3 mr-1" />
            Add another item
          </Button>

          <div className="mt-3">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Notes (optional)</label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. rent increased after lease renewal"
              data-testid="snapshot-notes"
            />
          </div>

          {/* iter138.9x — Effective From: defaults to today; backdate to
              apply the new total to Daily Entries in past months. */}
          <div className="mt-3">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1.5">
              <Calendar className="h-3 w-3" /> Effective From
            </label>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <Input
                type="date"
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
                max={todayIso}
                className="w-[180px]"
                data-testid="snapshot-effective-from"
              />
              {effectiveFrom && effectiveFrom !== todayIso && (
                <span
                  className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded px-2 py-1 flex items-center gap-1"
                  data-testid="snapshot-backdate-warning"
                >
                  <AlertCircle className="h-3 w-3" />
                  Backdating — will apply to Daily Entries from{' '}
                  <strong>{new Date(effectiveFrom + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}</strong>
                  {' '}onwards
                </span>
              )}
              {(!effectiveFrom || effectiveFrom === todayIso) && (
                <span className="text-[11px] text-slate-500">Defaults to today.</span>
              )}
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 pt-3 border-t border-slate-200">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Draft monthly</p>
              <p className="text-2xl font-black tabular-nums text-orange-700">{fmt(draftMonthly)}</p>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving || draftItems.every((it) => !it.name || Number(it.amount || 0) <= 0)}
              className="h-10 font-bold"
              data-testid="snapshot-save-btn"
            >
              <Lock className="h-4 w-4 mr-2" />
              {saving ? 'Saving…' : `Save as Version ${(list.snapshots[0]?.version_no || 0) + 1}`}
            </Button>
          </div>
        </Card>

        {/* Past versions list */}
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-600 font-bold mb-2 flex items-center gap-2">
            <DollarSign className="h-3.5 w-3.5" />
            All versions ({list.count})
          </p>
          {loading && <p className="text-sm text-slate-400">Loading…</p>}
          {!loading && list.snapshots.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-6">No versions yet — create v1 above.</p>
          )}
          <div className="space-y-2" data-testid="snapshot-history-list">
            {list.snapshots.map((s) => (
              <PastVersionCard
                key={s.snapshot_id}
                snap={s}
                expanded={expandedId === s.snapshot_id}
                onToggle={() => setExpandedId(expandedId === s.snapshot_id ? null : s.snapshot_id)}
                canDelete
                onDelete={() => handleDelete(s)}
              />
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
